/**
 * ADR-001 & ADR-110: Dynamic Regional Configuration
 * SINGLE SOURCE OF TRUTH for PK and UAE markets.
 */
import { API_BASE_URL } from '../config';

export type RegionCode = 'PK' | 'UAE';

export interface RegionalConfig {
  region: RegionCode;
  currency: { code: string; symbol: string; decimals: number; };
  locale: { code: string; dateFormat: string; timezone: string; };
  api: { port: number; url: string; };
  tax: { rate: number; label: string; };
}

const REGIONAL_CONFIGS: Record<RegionCode, RegionalConfig> = {
  PK: {
    region: 'PK',
    currency: { code: 'PKR', symbol: 'Rs', decimals: 2 },
    locale: { code: 'en-PK', dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Karachi' },
    api: { port: 0, url: API_BASE_URL }, // Port resolved dynamically via Vite proxy + ignition manifest
    tax: { rate: 0.17, label: 'Sales Tax' }
  },
  UAE: {
    region: 'UAE',
    currency: { code: 'AED', symbol: 'AED', decimals: 2 },
    locale: { code: 'en-AE', dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Dubai' },
    api: { port: 0, url: API_BASE_URL }, // Port resolved dynamically via Vite proxy + ignition manifest
    tax: { rate: 0.05, label: 'VAT' }
  }
};

export const detectRegion = (): RegionCode => {
  // 1. Host/Port dynamic detection for local and multi-instance environments
  if (typeof window !== 'undefined' && window.location) {
    const port = window.location.port;
    if (port === '3003' || port === '3001' || window.location.hostname.includes('uae')) {
      return 'UAE';
    }
    if (port === '3002' || port === '3000' || window.location.hostname.includes('pk')) {
      return 'PK';
    }
  }

  // 2. Fallback to Vite build-time environment variables
  const envRegion = import.meta.env.VITE_REGION || import.meta.env.VITE_REGION_CODE;
  if (envRegion === 'PK' || envRegion === 'UAE') return envRegion;
  return 'PK'; // Default fallback
};

export const getRegionalConfig = (): RegionalConfig => REGIONAL_CONFIGS[detectRegion()];

// S326 FIX: Updated signature to accept optional currency parameter
export const formatCurrency = (amount: number, currency?: string): string => {
  // Use provided currency OR fallback to regional config
  const currencyCode = currency || getRegionalConfig().currency.code;

  // Map currency to appropriate locale
  const LOCALE_MAP: Record<string, string> = {
    'PKR': 'en-PK',
    'AED': 'en-AE',
    'USD': 'en-US'
  };

  const locale = LOCALE_MAP[currencyCode] || 'en-US';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

export const regionalConfig = getRegionalConfig();
